import dayjs from "dayjs";

import { APPC_CONSENT } from "@/services/appc";
import { GetConsents } from "@/services/consent";
import { GetOrganizations } from "@/services/fhir";

/**
 * Code FHIR usado em `Organization.type` para farmácias / pharma.
 *
 * Não há ocorrência deste code no legado (`bus`, `prov` são os mais comuns).
 * Mantemos centralizado aqui — se o servidor usar outro code, altere só esta constante.
 */
export const PHARMA_ORGANIZATION_TYPE_CODE = "ecommerce";

/** Vigência "até revogação": no legado, `1000` vira `null` no APPC_CONSENT. */
const VIGENCY_UNTIL_REVOKE = null;

export type PharmaOrganizationItem = {
  /** Id FHIR completo (ex.: `${oids.cnpj}-123`). */
  id: string;
  /**
   * Identificador institucional sem prefixo OID — mesmo valor usado em
   * `contexto.instituicao` e em `APPC_CONSENT.cnes`.
   */
  institutionId: string;
  name: string;
};

export type ActiveOrganizationConsent = {
  institutionId: string;
};

export type PharmaConsentPreparation = {
  /** Orgs pharma que ainda precisam de consentimento (já autorizadas omitidas). */
  organizationsNeedingConsent: PharmaOrganizationItem[];
  /** Ids institucionais já com consentimento ativo. */
  alreadyConsentedInstitutionIds: string[];
  /**
   * True quando o paciente não tem nenhum consentimento pharma ativo.
   * UI deve pré-selecionar todas as orgs nesta lista.
   */
  hasNoPharmaConsent: boolean;
};

export type GrantOrganizationConsentsParams = {
  patientId: string;
  /** Lista de `institutionId` (sem OID) a autorizar. */
  institutionIds: string[];
};

export type GrantOrganizationConsentsResult = {
  granted: string[];
  failed: string[];
};

type ConsentRecord = {
  acao?: string;
  vigencia?: string;
  contexto?: {
    instituicao?: string;
  };
};

/**
 * Extrai o id institucional (pós-hífen) do id FHIR da Organization.
 * Espelha o padrão de ConsentsForDash / ConfirmConsentDialog: `id.split("-")[1]`.
 */
function extractInstitutionId(organizationId: string): string | null {
  const trimmed = organizationId.trim();
  if (!trimmed) return null;
  const parts = trimmed.split("-");
  if (parts.length < 2) return trimmed;
  return parts[1] || null;
}

function resolveOrganizationDisplayName(org: fhir5.Organization): string {
  const alias = org.alias?.[0];
  if (alias && alias !== "null") return alias;
  return org.name?.trim() || org.id || "Organização";
}

function isConsentStillValid(vigencia?: string): boolean {
  // Sem vigência = até revogação manual (válido).
  if (!vigencia) return true;
  const datePart = vigencia.split("T")[0];
  return dayjs(datePart).isAfter(dayjs());
}

function mapBundleToPharmaOrganizations(
  bundle: fhir5.Bundle | undefined,
): PharmaOrganizationItem[] {
  const entries = bundle?.entry ?? [];
  const items: PharmaOrganizationItem[] = [];

  for (const entry of entries) {
    const resource = entry.resource;
    if (!resource || resource.resourceType !== "Organization") continue;
    if (!resource.id) continue;

    const institutionId = extractInstitutionId(resource.id);
    if (!institutionId) continue;

    items.push({
      id: resource.id,
      institutionId,
      name: resolveOrganizationDisplayName(resource),
    });
  }

  return items;
}

/**
 * Lista organizações ativas do tipo pharma via FHIR ADM.
 */
async function listPharmaOrganizations(): Promise<PharmaOrganizationItem[]> {
  const response = (await GetOrganizations({
    type: PHARMA_ORGANIZATION_TYPE_CODE,
    active: true,
    count: 200,
  })) as fhir5.Bundle;

  return mapBundleToPharmaOrganizations(response);
}

/**
 * Consentimentos de instituição ativos (acao Autorizar + vigência válida).
 * Espelha o filtro de ConsentsForDash para organizações.
 */
async function getActiveOrganizationConsents(
  patientId: string,
): Promise<ActiveOrganizationConsent[]> {
  if (!patientId?.trim()) {
    throw new Error("patientId é obrigatório para buscar consentimentos.");
  }

  const response = await GetConsents({
    identifier: patientId,
    type: "granted",
  });

  const records = (response?.data ?? []) as ConsentRecord[];

  return records
    .filter((el) => el?.acao === "Autorizar")
    .filter((el) => Boolean(el?.contexto?.instituicao))
    .filter((el) => isConsentStillValid(el?.vigencia))
    .map((el) => ({
      institutionId: String(el.contexto!.instituicao),
    }));
}

/**
 * Cruza orgs pharma com consentimentos ativos.
 * Orgs já autorizadas são omitidas da lista de seleção (dialog mais simples).
 */
async function preparePharmaConsentSelection(
  patientId: string,
): Promise<PharmaConsentPreparation> {
  const [organizations, activeConsents] = await Promise.all([
    listPharmaOrganizations(),
    getActiveOrganizationConsents(patientId),
  ]);

  const consentedSet = new Set(
    activeConsents.map((c) => c.institutionId).filter(Boolean),
  );

  // Consent pharma = consentimento ativo cuja instituicao está no conjunto pharma.
  const pharmaInstitutionIds = new Set(
    organizations.map((org) => org.institutionId),
  );
  const alreadyConsentedInstitutionIds = [...consentedSet].filter((id) =>
    pharmaInstitutionIds.has(id),
  );

  const hasNoPharmaConsent = alreadyConsentedInstitutionIds.length === 0;

  const organizationsNeedingConsent = organizations.filter(
    (org) => !consentedSet.has(org.institutionId),
  );

  return {
    organizationsNeedingConsent,
    alreadyConsentedInstitutionIds,
    hasNoPharmaConsent,
  };
}

async function grantOrganizationConsents(
  params: GrantOrganizationConsentsParams,
): Promise<GrantOrganizationConsentsResult> {
  const patientId = params.patientId?.trim();
  if (!patientId) {
    throw new Error("patientId é obrigatório para conceder consentimento.");
  }

  const uniqueIds = [
    ...new Set(
      (params.institutionIds ?? [])
        .map((id) => id?.trim())
        .filter((id): id is string => Boolean(id)),
    ),
  ];

  const granted: string[] = [];
  const failed: string[] = [];

  for (const institutionId of uniqueIds) {
    try {
      await APPC_CONSENT({
        rule: "Permit",
        patient_id: patientId,
        subject_id: null,
        cnes: [institutionId],
        // Até revogação: legado envia null quando vigency === 1000.
        vigency: VIGENCY_UNTIL_REVOKE,
      });
      granted.push(institutionId);
    } catch (err) {
      console.error("grantOrganizationConsents falhou para", institutionId, err);
      failed.push(institutionId);
    }
  }

  return { granted, failed };
}

/** Verifica se o paciente já autorizou uma instituição específica. */
async function hasConsentForInstitution(
  patientId: string,
  institutionId: string,
): Promise<boolean> {
  if (!patientId?.trim() || !institutionId?.trim()) return false;
  const consents = await getActiveOrganizationConsents(patientId);
  return consents.some((c) => c.institutionId === institutionId);
}

export const pharmaConsentService = {
  listPharmaOrganizations,
  getActiveOrganizationConsents,
  preparePharmaConsentSelection,
  grantOrganizationConsents,
  hasConsentForInstitution,
};
